import { useState, useRef, useCallback, useEffect } from 'react';
import fixWebmDuration from 'fix-webm-duration';
import { containerDoMime } from './audioRecorderFormat';
import {
  atingiuTetoDeTamanho,
  escolherMimeTypeDeVideo,
  QUALIDADE_PADRAO,
  QUALIDADES,
  TETO_DA_GRAVACAO_BYTES,
  type QualidadeDeVideo
} from './screenRecorderFormat';

export interface RecordingOptions {
  includeWebcam: boolean;
  audioEnabled: boolean;
  quality?: QualidadeDeVideo;
}

export interface UseScreenRecorderReturn {
  isRecording: boolean;
  isPaused: boolean;
  recordingTime: number;
  videoBlob: Blob | null;
  error: string | null;
  // Tamanho já gravado e o teto em que a gravação para sozinha.
  bytesRecorded: number;
  maxBytes: number;
  // A gravação parou por ter chegado ao teto de tamanho, não por quem gravava.
  stoppedBySizeLimit: boolean;
  startRecording: (options: RecordingOptions) => Promise<void>;
  stopRecording: () => Promise<Blob | null>;
  pauseRecording: () => void;
  resumeRecording: () => void;
  cancelRecording: () => void;
}

// Relógio que continua batendo com a aba em segundo plano.
//
// Quem grava a tela quase sempre está olhando para outra janela, e aí o
// navegador congela `requestAnimationFrame` e segura `setInterval` (no Chrome,
// depois de alguns minutos, a um disparo por minuto). Era isso que deixava a
// gravação com webcam congelada e o cronômetro atrasado. Timer de Worker não
// sofre esse corte; o `setInterval` fica só de reserva.
function criarRelogio(intervaloMs: number, tick: () => void): () => void {
  if (typeof Worker !== 'undefined' && typeof URL.createObjectURL === 'function') {
    try {
      const url = URL.createObjectURL(
        new Blob([`setInterval(() => postMessage(0), ${intervaloMs});`], { type: 'text/javascript' })
      );
      const worker = new Worker(url);
      worker.onmessage = tick;
      return () => {
        worker.terminate();
        URL.revokeObjectURL(url);
      };
    } catch {
      // cai no setInterval abaixo
    }
  }
  const id = setInterval(tick, intervaloMs);
  return () => clearInterval(id);
}

function mensagemDeErro(err: unknown): string {
  if (!(err instanceof Error)) return 'Não foi possível iniciar a gravação';
  switch (err.name) {
    case 'NotAllowedError':
      return 'Permissão negada para capturar a tela. Por favor, permita o acesso.';
    case 'NotFoundError':
      return 'Nenhuma tela disponível para captura.';
    case 'NotSupportedError':
      return 'Captura de tela não suportada neste navegador.';
    case 'NotReadableError':
      return 'Não foi possível acessar a tela (já em uso?).';
    default:
      return err.message;
  }
}

function tracarRetanguloArredondado(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  largura: number,
  altura: number,
  raio: number
) {
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, largura, altura, raio);
    return;
  }
  ctx.moveTo(x + raio, y);
  ctx.lineTo(x + largura - raio, y);
  ctx.quadraticCurveTo(x + largura, y, x + largura, y + raio);
  ctx.lineTo(x + largura, y + altura - raio);
  ctx.quadraticCurveTo(x + largura, y + altura, x + largura - raio, y + altura);
  ctx.lineTo(x + raio, y + altura);
  ctx.quadraticCurveTo(x, y + altura, x, y + altura - raio);
  ctx.lineTo(x, y + raio);
  ctx.quadraticCurveTo(x, y, x + raio, y);
  ctx.closePath();
}

async function videoTocando(stream: MediaStream): Promise<HTMLVideoElement> {
  const video = document.createElement('video');
  // Mudo e inline: sem isso o autoplay é bloqueado e o frame fica preto.
  video.muted = true;
  video.playsInline = true;
  video.srcObject = stream;

  await new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error('Timeout ao carregar vídeos')), 5000);
    video.onloadedmetadata = () => {
      clearTimeout(timeout);
      resolve();
    };
  });
  await video.play();
  return video;
}

// Tela + webcam no canto inferior direito, compostos num canvas.
async function comporTelaComWebcam(
  screenStream: MediaStream,
  webcamStream: MediaStream,
  fps: number
): Promise<{ stream: MediaStream; parar: () => void }> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Não foi possível criar contexto do canvas');

  const [screenVideo, webcamVideo] = await Promise.all([
    videoTocando(screenStream),
    videoTocando(webcamStream)
  ]);

  canvas.width = screenVideo.videoWidth || 1920;
  canvas.height = screenVideo.videoHeight || 1080;

  // A webcam ocupa a mesma fração da tela em qualquer resolução: 320px fixos
  // cobririam um quarto de uma gravação em 720p.
  const largura = Math.round(canvas.width / 6);
  const altura = Math.round((largura * 3) / 4);
  const margem = Math.round(canvas.width / 96);
  const x = canvas.width - largura - margem;
  const y = canvas.height - altura - margem;

  const desenhar = () => {
    try {
      ctx.drawImage(screenVideo, 0, 0, canvas.width, canvas.height);
      ctx.save();
      tracarRetanguloArredondado(ctx, x, y, largura, altura, 10);
      ctx.clip();
      ctx.drawImage(webcamVideo, x, y, largura, altura);
      ctx.restore();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3;
      tracarRetanguloArredondado(ctx, x, y, largura, altura, 10);
      ctx.stroke();
    } catch (err) {
      console.error('Erro ao desenhar frame:', err);
    }
  };

  desenhar();
  const pararRelogio = criarRelogio(1000 / fps, desenhar);

  return {
    stream: canvas.captureStream(fps),
    parar: () => {
      pararRelogio();
      screenVideo.srcObject = null;
      webcamVideo.srcObject = null;
    }
  };
}

export const useScreenRecorder = (): UseScreenRecorderReturn => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bytesRecorded, setBytesRecorded] = useState(0);
  const [stoppedBySizeLimit, setStoppedBySizeLimit] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  // Tudo o que foi aberto (tela, webcam, microfone, canvas) e precisa ser
  // fechado — senão o navegador segue mostrando "compartilhando a tela".
  const streamsRef = useRef<MediaStream[]>([]);
  const pararComposicaoRef = useRef<(() => void) | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const bytesRef = useRef(0);
  const pendentesRef = useRef<Array<(blob: Blob | null) => void>>([]);
  // Muda a cada início e cancelamento. A finalização é assíncrona (correção da
  // duração); se a geração mudou no meio, o vídeo foi descartado e não volta.
  const geracaoRef = useRef(0);

  // O cronômetro mede o tempo ativo pelo relógio de parede, não somando
  // ticks: tick atrasado (aba em segundo plano) não atrasa a contagem, e a
  // pausa não zera o que já foi gravado.
  const pararRelogioRef = useRef<(() => void) | null>(null);
  const acumuladoMsRef = useRef(0);
  const inicioDoTrechoRef = useRef<number | null>(null);

  // O auto-stop (teto de tamanho, "Parar compartilhamento" do navegador) é
  // disparado de handlers criados antes de `stopRecording` existir.
  const pararRef = useRef<() => void>(() => {});

  const atualizarTempo = useCallback(() => {
    const trecho = inicioDoTrechoRef.current === null ? 0 : Date.now() - inicioDoTrechoRef.current;
    setRecordingTime(Math.floor((acumuladoMsRef.current + trecho) / 1000));
  }, []);

  const liberarRecursos = useCallback(() => {
    pararRelogioRef.current?.();
    pararRelogioRef.current = null;
    pararComposicaoRef.current?.();
    pararComposicaoRef.current = null;
    streamsRef.current.forEach(stream => stream.getTracks().forEach(track => track.stop()));
    streamsRef.current = [];
  }, []);

  const startRecording = useCallback(async (options: RecordingOptions) => {
    geracaoRef.current += 1;
    setError(null);
    setVideoBlob(null);
    setStoppedBySizeLimit(false);
    setBytesRecorded(0);
    setRecordingTime(0);
    chunksRef.current = [];
    bytesRef.current = 0;
    acumuladoMsRef.current = 0;
    inicioDoTrechoRef.current = null;

    try {
      if (!navigator.mediaDevices?.getDisplayMedia) {
        throw new Error('API de captura de tela não disponível neste navegador');
      }

      const mimeType = escolherMimeTypeDeVideo(
        tipo => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(tipo)
      );
      if (!mimeType) {
        throw new Error('Este navegador não suporta nenhum formato de vídeo compatível.');
      }

      const perfil = QUALIDADES[options.quality ?? QUALIDADE_PADRAO];

      // Resolução e fps limitados já na captura: gravar acima do perfil só
      // gastaria o teto de tamanho sem ganho visível na taxa escolhida.
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always',
          width: { max: perfil.largura },
          height: { max: perfil.altura },
          frameRate: { ideal: perfil.fps, max: perfil.fps }
        } as MediaTrackConstraints,
        audio: options.audioEnabled
          ? { echoCancellation: true, noiseSuppression: true }
          : false
      });
      streamsRef.current.push(screenStream);

      // Sem áudio do sistema (a pessoa não marcou "Compartilhar áudio"), o
      // microfone entra no lugar.
      let microfone: MediaStream | null = null;
      if (options.audioEnabled && screenStream.getAudioTracks().length === 0) {
        try {
          microfone = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            video: false
          });
          streamsRef.current.push(microfone);
        } catch (micError) {
          console.warn('Não foi possível capturar áudio do microfone:', micError);
        }
      }

      let videoTracks = screenStream.getVideoTracks();

      if (options.includeWebcam) {
        try {
          const webcam = await navigator.mediaDevices.getUserMedia({
            video: { width: { ideal: 320 }, height: { ideal: 240 } },
            audio: false
          });
          streamsRef.current.push(webcam);

          const composicao = await comporTelaComWebcam(screenStream, webcam, perfil.fps);
          streamsRef.current.push(composicao.stream);
          pararComposicaoRef.current = composicao.parar;
          videoTracks = composicao.stream.getVideoTracks();
        } catch (webcamError) {
          console.error('Erro ao capturar webcam:', webcamError);
          setError('Não foi possível acessar a webcam. Gravando apenas a tela.');
        }
      }

      // O MediaRecorder grava uma track de áudio só: a do sistema, ou a do
      // microfone quando não há a do sistema.
      const audioTracks = options.audioEnabled
        ? [...screenStream.getAudioTracks(), ...(microfone?.getAudioTracks() ?? [])].slice(0, 1)
        : [];

      if (options.audioEnabled && audioTracks.length === 0) {
        setError('Aviso: Gravação sem áudio. Permita o acesso ao microfone ou marque "Compartilhar áudio" ao compartilhar a tela.');
      }

      const finalStream = new MediaStream([...videoTracks, ...audioTracks]);

      const recorder = new MediaRecorder(finalStream, {
        mimeType,
        videoBitsPerSecond: perfil.bitrateDeVideo,
        audioBitsPerSecond: perfil.bitrateDeAudio
      });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = event => {
        if (!event.data || event.data.size === 0) return;
        chunksRef.current.push(event.data);
        bytesRef.current += event.data.size;
        setBytesRecorded(bytesRef.current);

        // Parar aqui, com folga, em vez de deixar o upload recusar depois:
        // assim o que foi gravado nunca se perde.
        if (atingiuTetoDeTamanho(bytesRef.current) && recorder.state !== 'inactive') {
          setStoppedBySizeLimit(true);
          pararRef.current();
        }
      };

      // Único ponto de finalização: vale para "Parar", teto de tamanho,
      // "Parar compartilhamento" do navegador e erro do MediaRecorder (que
      // também dispara stop). O mime do Blob vai sem codecs porque vira o
      // content-type do upload, e o bucket só lista containers.
      recorder.onstop = async () => {
        const geracao = geracaoRef.current;
        if (inicioDoTrechoRef.current !== null) {
          acumuladoMsRef.current += Date.now() - inicioDoTrechoRef.current;
          inicioDoTrechoRef.current = null;
        }
        atualizarTempo();

        const tipo = containerDoMime(recorder.mimeType || mimeType);
        const bruto = new Blob(chunksRef.current, { type: tipo });
        mediaRecorderRef.current = null;
        chunksRef.current = [];
        liberarRecursos();

        // O webm do MediaRecorder sai sem a duração no cabeçalho: o player
        // mostra duração infinita e não deixa avançar nem voltar. A correção
        // grava a duração medida; se falhar, o vídeo segue sem ela, mas segue.
        let blob = bruto;
        if (tipo === 'video/webm') {
          try {
            const corrigido = await fixWebmDuration(bruto, acumuladoMsRef.current, { logger: false });
            blob = new Blob([corrigido], { type: tipo });
          } catch (err) {
            console.warn('Não foi possível gravar a duração no webm:', err);
          }
        }
        if (geracao !== geracaoRef.current) return;

        setVideoBlob(blob);
        setIsRecording(false);
        setIsPaused(false);

        const pendentes = pendentesRef.current;
        pendentesRef.current = [];
        pendentes.forEach(resolve => resolve(blob));
      };

      recorder.onerror = event => {
        console.error('Erro no MediaRecorder:', event);
        setError('A gravação foi interrompida por um erro. O que já foi gravado foi mantido.');
      };

      screenStream.getVideoTracks()[0].onended = () => pararRef.current();

      // Chunks de 1s: o teto de tamanho é conferido a cada segundo.
      recorder.start(1000);
      inicioDoTrechoRef.current = Date.now();
      pararRelogioRef.current = criarRelogio(500, atualizarTempo);
      setIsRecording(true);
      setIsPaused(false);
    } catch (err) {
      console.error('Erro ao iniciar gravação:', err);
      setError(mensagemDeErro(err));
      mediaRecorderRef.current = null;
      liberarRecursos();
    }
  }, [atualizarTempo, liberarRecursos]);

  const stopRecording = useCallback(async (): Promise<Blob | null> => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return null;

    return new Promise<Blob | null>(resolve => {
      pendentesRef.current.push(resolve);
      recorder.stop();
    });
  }, []);

  const pauseRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder?.state !== 'recording') return;

    recorder.pause();
    if (inicioDoTrechoRef.current !== null) {
      acumuladoMsRef.current += Date.now() - inicioDoTrechoRef.current;
      inicioDoTrechoRef.current = null;
    }
    atualizarTempo();
    setIsPaused(true);
  }, [atualizarTempo]);

  const resumeRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder?.state !== 'paused') return;

    recorder.resume();
    inicioDoTrechoRef.current = Date.now();
    setIsPaused(false);
  }, []);

  const cancelRecording = useCallback(() => {
    geracaoRef.current += 1;
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      // Sem handlers: a gravação descartada não pode virar Blob — antes ela
      // virava, e o modal reabria em "preview" com um vídeo vazio.
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.stop();
    }

    const pendentes = pendentesRef.current;
    pendentesRef.current = [];
    pendentes.forEach(resolve => resolve(null));

    mediaRecorderRef.current = null;
    chunksRef.current = [];
    bytesRef.current = 0;
    acumuladoMsRef.current = 0;
    inicioDoTrechoRef.current = null;
    liberarRecursos();
    setVideoBlob(null);
    setIsRecording(false);
    setIsPaused(false);
    setRecordingTime(0);
    setBytesRecorded(0);
    setStoppedBySizeLimit(false);
  }, [liberarRecursos]);

  useEffect(() => {
    pararRef.current = () => {
      void stopRecording();
    };
  }, [stopRecording]);

  // Desmontar no meio da gravação não pode deixar tela, câmera ou microfone
  // abertos.
  useEffect(() => {
    return () => {
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.onstop = null;
        recorder.stop();
      }
      liberarRecursos();
    };
  }, [liberarRecursos]);

  return {
    isRecording,
    isPaused,
    recordingTime,
    videoBlob,
    error,
    bytesRecorded,
    maxBytes: TETO_DA_GRAVACAO_BYTES,
    stoppedBySizeLimit,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
  };
};
